import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import styled from 'styled-components'
import moment from 'moment'

import { Text } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { Gusto_Payroll } from '../generated_gusto_types'
import { TypographyStyles } from '@/styles'
import { Colors, Fonts } from '../../../styles/theme'

const PAY_PERIOD_COLOR = 'blush'
const DEADLINE_COLOR = 'orange'
const PAYDAY_COLOR = 'teal'

const Calendar = styled(
  ({
    payroll: {
      pay_period: { start_date, end_date },
    },
    className,
    monthsShown,
  }: {
    payroll: Gusto_Payroll
    className?: string
    monthsShown: number
  }) => (
    <ReactDatePicker
      calendarClassName={className}
      onChange={() => {
        // This function is required by date picker but we don't need to do selecting
      }}
      disabled
      inline
      // Set so the calendar knows which date start displaying at
      selected={moment(start_date, DATE_FORMATS.GUSTO_SUBMIT).toDate()}
      startDate={moment(start_date, DATE_FORMATS.GUSTO_SUBMIT).toDate()}
      endDate={moment(end_date, DATE_FORMATS.GUSTO_SUBMIT).toDate()}
      renderCustomHeader={({ monthDate }) => (
        <Text variant={TypographyStyles.H3}>
          {moment(monthDate).format(DATE_FORMATS.MONTH_LONG)}
        </Text>
      )}
      monthsShown={monthsShown}
      dayClassName={(date) =>
        `day-${moment(date).format(DATE_FORMATS.MONTH_DAY_NUMERIC)}`
      }
    />
  )
)(({ payroll: { payroll_deadline, check_date } }) => {
  const deadlineDay = moment(
    payroll_deadline,
    DATE_FORMATS.GUSTO_SUBMIT
  ).format(DATE_FORMATS.MONTH_DAY_NUMERIC)
  const checkDay = moment(check_date, DATE_FORMATS.GUSTO_SUBMIT).format(
    DATE_FORMATS.MONTH_DAY_NUMERIC
  )

  return {
    // This calendar should not be selectable
    pointerEvents: 'none',
    border: 'none',
    borderRadius: 8,
    backgroundColor: Colors.transparent,
    ...Fonts.eyebrow,

    '.react-datepicker__header': {
      backgroundColor: Colors.transparent,
    },
    '.react-datepicker__month-container': {
      width: '100% !important',
    },

    '.react-datepicker__day-name': {
      color: Colors.mediumGray,
      // Padding adjusted here to match dates
      width: 21,
      margin: '0 6px 2px 0',
    },

    '.react-datepicker__day': {
      color: Colors.black,
      // Padding adjusted so there aren't spaces in selections
      margin: '2px 0',
      width: 26,
      height: 26,
      paddingTop: 2,

      '&.react-datepicker__day--in-range': {
        backgroundColor: Colors[PAY_PERIOD_COLOR],
        borderRadius: 0,
      },

      '&.react-datepicker__day--outside-month': {
        color: Colors.mediumGray,
      },

      // Prevents marking days on the wrong month
      '&:not(.react-datepicker__day--outside-month)': {
        [`&.day-${deadlineDay}`]: {
          backgroundColor: Colors[DEADLINE_COLOR],
          color: Colors.white,
          borderRadius: 2,
        },

        [`&.day-${checkDay}`]: {
          backgroundColor: Colors[PAYDAY_COLOR],
          color: Colors.white,
          borderRadius: 2,
        },
      },

      // Spacing changes cause empty cells to mess wth padding
      '&:empty': {
        height: 0,
        padding: 0,
      },
    },
  }
})

const LegendMarker = styled(
  ({ className }: { className?: string; color: keyof typeof Colors }) => (
    <div className={className} />
  )
)(({ color }) => ({
  height: 10,
  width: 10,
  backgroundColor: Colors[color],
  borderRadius: 2,
  marginRight: 10,
}))

const LegendItem = ({
  text,
  color,
}: {
  text: string
  color: keyof typeof Colors
}) => (
  <div className="flex items-center">
    <LegendMarker color={color} />
    <Text variant={TypographyStyles.BODY_MD}>{text}</Text>
  </div>
)

export const PayrollCalendar = ({ payroll }: { payroll: Gusto_Payroll }) => {
  const {
    pay_period: { start_date, end_date },
    payroll_deadline,
    check_date,
  } = payroll

  const monthDiff = moment(check_date, DATE_FORMATS.GUSTO_SUBMIT)
    .endOf('months')
    .diff(
      moment(start_date, DATE_FORMATS.GUSTO_SUBMIT).startOf('months'),
      'months'
    )

  return (
    <div className="flex flex-col gap-4 text-left">
      <Text variant={TypographyStyles.H1}>Pay period</Text>
      <Text variant={TypographyStyles.H3}>
        {moment(start_date, DATE_FORMATS.GUSTO_SUBMIT).format(
          DATE_FORMATS.DISPLAY_SHORT
        )}{' '}
        -{' '}
        {moment(end_date, DATE_FORMATS.GUSTO_SUBMIT).format(
          DATE_FORMATS.DISPLAY_SHORT
        )}
      </Text>

      <div className="flex flex-col md:flex-row gap-6">
        <div className={`w-full md:w-${monthDiff ? '2/3' : '1/2'}`}>
          <Calendar payroll={payroll} monthsShown={monthDiff ? 2 : 1} />
        </div>

        <div className="w-full md:w-1/2 flex flex-col gap-3">
          <Text variant={TypographyStyles.BODY_MD}>Dates of interest</Text>
          <LegendItem text="Payroll period" color={PAY_PERIOD_COLOR} />
          <LegendItem
            text={`${moment(payroll_deadline, DATE_FORMATS.GUSTO_SUBMIT).format(DATE_FORMATS.MONTH_DAY)}: Payroll Deadline`}
            color={DEADLINE_COLOR}
          />
          <LegendItem
            text={`${moment(check_date, DATE_FORMATS.GUSTO_SUBMIT).format(DATE_FORMATS.MONTH_DAY)}: Payday`}
            color={PAYDAY_COLOR}
          />
        </div>
      </div>
    </div>
  )
}
